import { useCallback, useContext } from 'react';

import { AxiosResponse } from 'axios';

import { CompanyRolesResponseDTO } from '@vyce/core/src/api/types';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { PAY_PERMISSIONS, TEAMS_PERMISSIONS, TIME_INTERFACE_PERMISSIONS } from '@vyce/core/src/types';
import {
  fetchStaffModuleResources,
  StaffModuleResourceAccess,
  StaffModuleResources,
} from '@vyce/core/src/utils/staffPermissions';

export interface CompanyRolesData {
  roles: CompanyRolesResponseDTO;
  resources: StaffModuleResources;
  /**
   * Which inventories were actually fetched. An inventory the viewer may not read
   * comes back as an empty array, which would otherwise be indistinguishable from
   * a company that genuinely has no teams — and the two must lead to different UI.
   */
  readable: Required<StaffModuleResourceAccess>;
}

// roles/available and the teams/sites/pay-schedules inventory are company-level:
// identical for every staff member in the list. Without this cache a screen with
// N expanded rows fires 4N requests for the same four answers. Same pattern as
// usePermissionCatalogue — a module-level map plus in-flight dedupe, since there
// is no react-query in this repo.
const cache = new Map<string, CompanyRolesData>();
const inFlight = new Map<string, Promise<CompanyRolesData>>();

// For when the server says a cached role no longer exists — the next fetch reloads.
export const invalidateCompanyRolesData = (companyId: string) => cache.delete(companyId);

export const useCompanyRolesData = () => {
  const { listAvailableRolesRequest, fetchModuleResources = fetchStaffModuleResources } =
    useContext(RolesModuleContext);
  const { canAnywhere } = usePermissions();

  // Reading a resource inventory is its own right — don't fire a search the
  // viewer is not allowed to make just to populate a scope picker they can't use.
  const access: Required<StaffModuleResourceAccess> = {
    sites: canAnywhere(TIME_INTERFACE_PERMISSIONS.LOCATIONS_AND_SHIFTS),
    teams: canAnywhere(TEAMS_PERMISSIONS.CAN_VIEW_TEAMS),
    paySchedules: canAnywhere(PAY_PERMISSIONS.READ_PAY_SCHEDULES),
  };

  const fetchCompanyRolesData = useCallback(
    async (companyId: string): Promise<CompanyRolesData> => {
      const cached = cache.get(companyId);
      if (cached) return cached;

      const pending =
        inFlight.get(companyId) ||
        Promise.all([
          listAvailableRolesRequest(companyId) as Promise<AxiosResponse<CompanyRolesResponseDTO>>,
          fetchModuleResources(companyId, access),
        ]).then(([rolesRes, resources]) => ({ roles: rolesRes.data, resources, readable: access }));

      inFlight.set(companyId, pending);
      try {
        const data = await pending;
        cache.set(companyId, data);
        return data;
      } finally {
        inFlight.delete(companyId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listAvailableRolesRequest, fetchModuleResources, access.sites, access.teams, access.paySchedules]
  );

  return { fetchCompanyRolesData };
};
